import { type PropsWithChildren, useRef } from 'react'

import { CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as Sentry from '@sentry/react'
import { HttpStatusCode } from 'axios'
import { useSnackbar } from 'notistack'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'
import { useTimeout } from 'usehooks-ts'

import { checkCurrentSession, checkSessionToken } from '~/api'
import { getRefreshExpiry } from '~/features/Auth/utils/refresh-expiry'
import store from '~/redux'
import { updateDraft } from '~/redux/slices/currentDraft'
import { updateSurvey } from '~/redux/slices/currentSurvey'
import { updateUser } from '~/redux/slices/user'
import Login from '~/screens/Login'

import { axios } from './axios'
import PasswordWarningModal from './components/modals/PasswordWarningModal'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import {
  validateDynamicLinkToken,
  validateFamilyDynamicLinkToken,
} from './services/dynamic-link'
import { op } from './utils/open-panel-utils'

function getSessionKey(user: {
  username: string
  refreshTokenExpiresAt?: number | null
  sessionStart: number
}) {
  return `${user.username}:${user.refreshTokenExpiresAt ?? user.sessionStart}`
}

function LoadingAuth() {
  const classes = useLoadingAuthStyles()

  return (
    <div className={classes.container}>
      <CircularProgress />
    </div>
  )
}

const useLoadingAuthStyles = makeStyles(_theme => ({
  container: {
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

export default function Authenticator({ children }: PropsWithChildren<object>) {
  const { t, i18n } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const user = useAppSelector(state => state.user)

  const dispatch = useAppDispatch()
  const intercom = useIntercom()
  const navigate = useNavigate()
  const location = useLocation()

  const [authVerified, setAuthVerified] = useState(false)
  const [isCheckingDynamicLink, setIsCheckingDynamicLink] = useState(false)
  const validatedSessionKey = useRef<string | null>(null)
  const warnedRefreshExpiry = useRef<number | null>(null)
  const checkingLink = useRef<string | null>(null)

  const refreshExpiryMs = getRefreshExpiry(user?.refreshTokenExpiresAt)

  const now = Date.now()
  const expireTime = refreshExpiryMs ? Math.max(0, refreshExpiryMs - now) : null

  const warningTime =
    expireTime === null ? null : Math.max(0, expireTime - 30 * 60 * 1000)

  const searchParam = useMemo(
    () => new URLSearchParams(location.search),
    [location],
  )

  /**
   * Used for user activation via email invitation with this shape:
   * @example
   * https://testing.povertystoplight.org/users/activate/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
   */
  const isActivateUserScreen = location.pathname.includes('/activate/')
  const isOktaSsoScreen = location.pathname === '/okta-sso'

  /**
   * Used for taking surveys with just a link with this shape:
   * @example
   * http://testing.povertystoplight.org/?token=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
   */
  const isLoginScreen = location.pathname.includes('/login')
  const dynamicLinkToken = !isLoginScreen ? searchParam.get('token') : null
  const familyLinkToken = !isLoginScreen ? searchParam.get('fid') : null
  const linkToken = familyLinkToken ?? dynamicLinkToken
  const hasUserToken = !!user?.token

  const endSession = useCallback(() => {
    setAuthVerified(false)
    dispatch(updateUser(null))
    axios.defaults.headers.post['Stoplight-Client-Id'] = 'stoplight-web'
    setIsCheckingDynamicLink(false)
    navigate('/login', { replace: true })
  }, [dispatch, navigate])

  useTimeout(() => {
    if (!hasUserToken) return
    enqueueSnackbar(t('views.login.expired'), { variant: 'error' })
    endSession()
  }, expireTime)

  useTimeout(() => {
    if (!hasUserToken) return
    if (refreshExpiryMs === null) return
    if (warnedRefreshExpiry.current === refreshExpiryMs) return

    enqueueSnackbar(t('views.login.warning'), { variant: 'warning' })
    warnedRefreshExpiry.current = refreshExpiryMs
  }, warningTime)

  useEffect(() => {
    if (!user?.token) return

    intercom.update({
      name: user.name,
      email: user.email,
      userId: user.username,
      createdAt: Math.floor(Date.now() / 1000),
      languageOverride: i18n.language,
    })
  }, [user, intercom, i18n])

  useEffect(() => {
    if (!user?.token) return

    // Older persisted sessions may not have the normalized numeric `id` yet.
    // Keep analytics initialization from crashing while the session is refreshed.
    const profileId = String(user.id ?? user.username)

    void op.identify({
      firstName: user.username,
      profileId,
      properties: {
        ...user,
      },
    })

    Sentry.setUser({
      id: user.id,
      username: user.username,
      email: user.email,
    })
  }, [user])

  useEffect(() => {
    if (!user?.token) {
      validatedSessionKey.current = null
      warnedRefreshExpiry.current = null
      setAuthVerified(false)
      return
    }

    if (linkToken ?? user.dynamicLink) {
      setAuthVerified(true)
      return
    }

    const sessionKey = getSessionKey(user)
    if (validatedSessionKey.current === sessionKey) return

    validatedSessionKey.current = sessionKey
    warnedRefreshExpiry.current = null
    setAuthVerified(false)

    // Verifying token before logging user in
    checkCurrentSession()
      .then(response => {
        const currentUser = store.getState().user

        if (!currentUser || getSessionKey(currentUser) !== sessionKey) {
          return
        }

        dispatch(
          updateUser({
            ...currentUser,
            permissions: response.data.permissions,
            username: response.data.username,
            id: response.data.userId,
            role: response.data.role,
            hub: response.data.application,
            organization: response.data.organization,
            stakeholder: response.data.stakeholder,
            name: response.data.name,
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
            interactive_help:
              !!response.data.application &&
              !!response.data.application.interactiveHelp,
            sessionExpired: false,
            sessionStart: currentUser.sessionStart || new Date().getTime(),
            dynamicLink: null,
            sso: response.data.sso,
          }),
        )
        setAuthVerified(true)
      })
      .catch(error => {
        const currentUser = store.getState().user
        if (!currentUser) {
          endSession()
          return
        }

        if (getSessionKey(currentUser) !== sessionKey) return

        if (
          axios.isAxiosError(error) &&
          (error.response?.status === HttpStatusCode.Unauthorized ||
            error.response?.status === HttpStatusCode.Forbidden)
        ) {
          endSession()
          return
        }

        setAuthVerified(true)
      })
  }, [dispatch, linkToken, endSession, user])

  useEffect(() => {
    if (!linkToken) return

    if (
      user?.dynamicLink &&
      !familyLinkToken &&
      !user.dynamicLink.fid &&
      user.dynamicLink.token === dynamicLinkToken
    )
      return

    if (user?.token && !user?.dynamicLink) {
      enqueueSnackbar(t('dynamicLink.warningUserLoggedIn'), {
        variant: 'warning',
      })

      return
    }

    const requestKey = `${familyLinkToken ? 'fid' : 'token'}:${linkToken}`
    if (checkingLink.current === requestKey) return
    checkingLink.current = requestKey

    async function checkDynamicLinkToken() {
      try {
        if (!linkToken) return

        setIsCheckingDynamicLink(true)
        const data = familyLinkToken
          ? await validateFamilyDynamicLinkToken(familyLinkToken)
          : await validateDynamicLinkToken(linkToken)
        await checkSessionToken(data.accessToken)
          .then(response => {
            dispatch(updateDraft(null))
            dispatch(updateSurvey(null))
            dispatch(
              updateUser({
                ...user,
                permissions: response.data.permissions,
                username: response.data.username,
                id: response.data.userId,
                token: data.accessToken,
                refreshToken: '',
                refreshTokenExpiresAt: null,
                env: data.env,
                willExpire: user?.willExpire ?? false,
                didExpire: user?.didExpire ?? false,
                daysToExpirePassword: user?.daysToExpirePassword ?? 0,
                role: response.data.role,
                hub: response.data.application,
                organization: response.data.organization,
                stakeholder: response.data.stakeholder,
                name: response.data.name,
                email: response.data.email,
                phoneNumber: response.data.phoneNumber ?? '',
                interactive_help:
                  !!response.data.application &&
                  !!response.data.application.interactiveHelp,
                sessionExpired: false,
                sessionStart: user?.sessionStart
                  ? user.sessionStart
                  : new Date().getTime(),
                dynamicLink: { ...data },
                sso: response.data.sso,
              }),
            )
            setAuthVerified(true)
            navigate('/welcome', { replace: true })

            /**
             * Setting a specific header to identify all requests as coming
             * from a dynamic link
             *
             * **Note:** This requires a cleanup on logout
             */
            axios.defaults.headers.post['Stoplight-Client-Id'] =
              'stoplight-web-dynamic-link'
          })
          .catch(() => {
            endSession()
          })
          .finally(() => {
            setIsCheckingDynamicLink(false)
          })
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === HttpStatusCode.NotFound) {
            enqueueSnackbar(t('dynamicLink.errorSurveyNotFound'), {
              variant: 'error',
            })
            endSession()
            return
          }

          if (error.response?.status === HttpStatusCode.Unauthorized) {
            enqueueSnackbar('Token no longer valid', { variant: 'error' })
            endSession()
            return
          }
        }

        enqueueSnackbar(t('general.error'), { variant: 'error' })
        endSession()
      } finally {
        checkingLink.current = null
        setIsCheckingDynamicLink(false)
      }
    }

    void checkDynamicLinkToken()
    // NOTE: For some reason `t` triggers 2 updates
  }, [
    dispatch,
    enqueueSnackbar,
    dynamicLinkToken,
    familyLinkToken,
    linkToken,
    endSession,
    navigate,
    t,
    user,
  ])

  return (
    <>
      <PasswordWarningModal />

      {((authVerified && hasUserToken && !isCheckingDynamicLink) ||
        isActivateUserScreen ||
        isOktaSsoScreen) && <>{children}</>}
      {!authVerified &&
        !hasUserToken &&
        !isActivateUserScreen &&
        !isOktaSsoScreen &&
        !isCheckingDynamicLink && <Login />}

      {((!authVerified && hasUserToken) || isCheckingDynamicLink) && (
        <LoadingAuth />
      )}
    </>
  )
}
